<template>
    <div class="tw-overflow-x-auto">
      <table
        class="tw-divide-y tw-divide-gray-200 tw-bg-white tw-border-seperate tw-rounded-[6px]"
      >
        <thead>
          <tr>
            <th
              v-for="header in tableHead"
              :key="header.key"
              class="tw-px-6 tw-py-5 tw-text-left tw-border-b tw-border-[#E1E6F0] tw-text-xs tw-font-medium tw-text-[#4F5877] tw-uppercase tw-tracking-wider"
            >
              {{ header.label }}
            </th>
          </tr>
        </thead>
        <tbody class="tw-divide-y tw-divide-gray-200">
          <tr v-if="loans.length === 0">
            <td
              colspan="7"
              class="tw-px-6 tw-py-5 tw-whitespace-nowrap tw-text-sm tw-text-gray-500 tw-text-center"
            >
              <EmptyState v-if="loans.length === 0">
                <template v-slot:message>
                  <p class="message tw-text-center tw-text-gray tw-my-3">
                    There are no loans to display for this borrower.
                  </p>
                </template>
              </EmptyState>
            </td>
          </tr>
          <tr v-for="loan in loans" :key="loan.id">
            <td
              class="tw-px-6 tw-py-5 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-[#323E67]"
            >
              ₦{{ loan.amount.toLocaleString() }}
            </td>
            <td
              class="tw-px-6 tw-py-5 tw-whitespace-nowrap tw-text-sm tw-text-gray-500"
            >
              {{ loan.duration }}
            </td>
            <td
              class="tw-px-6 tw-py-5 tw-whitespace-nowrap tw-text-sm tw-text-gray-500"
            >
              {{ loan.interest_rate * 100 }}%
            </td>
            <td
              class="tw-px-6 tw-py-5 tw-whitespace-nowrap tw-text-sm tw-text-[#323E67]"
            >
              ₦{{ loan.repayment_amount.toLocaleString() }}
            </td>
            <td
              class="tw-px-6 tw-py-5 tw-whitespace-nowrap tw-text-sm tw-text-[#323E67]"
            >
              <div class="tw-flex tw-items-center">
                {{ formatDate(loan.repayment_date) }}
                <span
                  v-if="isOverdue(loan)"
                  class="tw-ml-2 tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-bg-[#DB4343] tw-text-white"
                >
                  Overdue
                </span>
              </div>
            </td>
            <td
              class="tw-px-6 tw-py-5 tw-whitespace-nowrap tw-text-sm"
            >
              <span class="status-badge" :class="statusClass(loan.status)">
                {{ formatStatus(loan.status) }}
              </span>
            </td>
            <td
            class="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-[#4D84FF]"
          >
            <button
              @click="$emit('loanDetails', loan)"
              class="tw-text-[#4D84FF] hover:tw-bg-transparent hover:tw-text-[#4D84FF]"
            >
              View loan details
            </button>
          </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
  
  <script setup>
  import { ref } from "vue";
  import EmptyState from "@/components/notification/EmptyState.vue";
  
  defineProps({
    loans: {
      type: Array,
      required: true,
    },
  });
  
  defineEmits(["repay", "loanDetails"]);
  
  const tableHead = ref([
    { key: "amount", label: "Credit Amount" },
    { key: "duration", label: "Duration (months)" },
    { key: "interest", label: "Interest" },
    { key: "repaymentAmount", label: "Repayment Amount" },
    { key: "repaymentDate", label: "Repayment Date" },
    { key: "status", label: "Status" },
    { key: "actions", label: "" },
  ]);

  function formatStatus(status) {
    if (!status) return "—";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  function statusClass(status) {
    switch ((status || "").toUpperCase()) {
      case "REPAID":
      case "PAID":
      case "COMPLETED":
        return "status--success";
      case "OVERDUE":
      case "DEFAULTED":
        return "status--danger";
      case "PENDING":
        return "status--pending";
      default:
        return "status--active";
    }
  }

  function formatDate(date) {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString();
  };
  
  function hasDatePassed(dateString) {
    const inputDate = new Date(dateString);
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    return inputDate < today;
  }

  function isSettled(status) {
    return ["REPAID", "PAID", "COMPLETED"].includes((status || "").toUpperCase());
  }

  // A settled (completed/repaid) loan is never overdue.
  function isOverdue(loan) {
    return !isSettled(loan.status) && hasDatePassed(loan.repayment_date);
  }
  
  </script>

  <style lang="scss" scoped>
    table{
        min-width: 100%;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
    }

    .status--active {
      background: #e5edff;
      color: #2a62f5;
    }

    .status--success {
      background: #e7f6ec;
      color: #1f9254;
    }

    .status--danger {
      background: #fbeaea;
      color: #db4343;
    }

    .status--pending {
      background: #fef3e0;
      color: #b25e09;
    }
  </style>